import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Upload, Trash2, Download, Share2, Link2, Copy, RefreshCw, Plus, X, FileText, Image as ImageIcon, Lock, Printer, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { API, API_URL, formatApiError } from "@/lib/api";
import { TIPI_PRATICA, STATI_PRATICA, STATO_STYLES, tipoLabel, statoLabel, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LocationSelect from "@/components/LocationSelect";
import { useStudio } from "@/context/StudioContext";
import { PDFDocument, PDFTextField, PDFCheckBox, PDFDropdown, PDFOptionList, PDFRadioGroup } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import { renderAsync as renderDocx } from "docx-preview";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

async function createPrintedPdfBlob(bytes) {
  const source=await pdfjsLib.getDocument({data:bytes instanceof ArrayBuffer?bytes:bytes.slice().buffer}).promise;
  let output=null;
  for(let number=1;number<=source.numPages;number++){
    const page=await source.getPage(number),base=page.getViewport({scale:1}),view=page.getViewport({scale:1.6});
    const canvas=document.createElement("canvas");canvas.width=view.width;canvas.height=view.height;
    await page.render({canvasContext:canvas.getContext("2d"),viewport:view}).promise;
    const orientation=base.width>base.height?"landscape":"portrait";
    if(!output)output=new jsPDF({orientation,unit:"pt",format:[base.width,base.height]});else output.addPage([base.width,base.height],orientation);
    output.addImage(canvas.toDataURL("image/jpeg",0.96),"JPEG",0,0,base.width,base.height,undefined,"FAST");
  }
  return output.output("blob");
}

function FileTransferActions({ url, filename, title, shareEnabled = true }) {
  const [busy, setBusy] = useState(false);

  const getFile = async () => {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) throw new Error("Download non riuscito");
    const blob = await response.blob();
    return new File([blob], filename || "documento", { type: blob.type || "application/octet-stream" });
  };

  const share = async () => {
    setBusy(true);
    try {
      const file = await getFile();
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ title: title || filename, files: [file] });
      } else {
        const objectUrl = URL.createObjectURL(file);
        const anchor = document.createElement("a");
        anchor.href = objectUrl; anchor.download = file.name; anchor.click();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Impossibile condividere il file");
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const file = await getFile();
      const objectUrl = URL.createObjectURL(file);
      const anchor = document.createElement("a");
      anchor.href = objectUrl; anchor.download = file.name;
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch { toast.error("Impossibile scaricare il file"); }
    finally { setBusy(false); }
  };

  return <>
    {shareEnabled && <button type="button" disabled={busy} title="Condividi o salva sul telefono" onClick={share} className="text-blue-800 hover:text-blue-900 disabled:opacity-40"><Share2 className="h-4 w-4" /></button>}
    <button type="button" disabled={busy} title="Scarica" onClick={download} className="text-blue-800 hover:text-blue-900 disabled:opacity-40"><Download className="h-4 w-4" /></button>
  </>;
}

export default function PraticaDetail() {
  const { id } = useParams();
  const [practice, setPractice] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [activeTab, setActiveTab] = useState("dati");
  const attachmentsRequestRef = useRef(0);
  const { settings } = useStudio();
  const practiceTypes = settings?.practice_types?.length ? settings.practice_types : TIPI_PRATICA;

  const loadAll = async () => {
    const requestId = ++attachmentsRequestRef.current;
    try {
      const [p, a, c] = await Promise.all([
        API.get(`/practices/${id}`),
        API.get(`/practices/${id}/attachments`),
        API.get(`/practices/${id}/comments`),
      ]);
      if(requestId===attachmentsRequestRef.current){setPractice(p.data);setAttachments(a.data);setComments(c.data);}
    } catch (e) {
      toast.error(formatApiError(e, "Pratica non trovata"));
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const syncPortalChanges = async () => {
      if (document.visibilityState !== "visible") return;
      const requestId = ++attachmentsRequestRef.current;
      try {
        const [a, c] = await Promise.all([
          API.get(`/practices/${id}/attachments`),
          API.get(`/practices/${id}/comments`),
        ]);
        if(requestId===attachmentsRequestRef.current){setAttachments(a.data);setComments(c.data);}
      } catch { /* il normale caricamento gestisce gli errori di sessione */ }
    };
    const timer = window.setInterval(syncPortalChanges, 5000);
    window.addEventListener("focus", syncPortalChanges);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", syncPortalChanges); };
  }, [id]);

  if (!practice) {
    return (
      <div className="flex justify-center py-24" data-testid="pratica-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-800 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pratica-detail-page">
      <div className="print-header hidden">
        {settings?.logo_url && <img src={settings.logo_url} alt="Logo studio" />}
        <div><strong>{settings?.studio_name}</strong><br />{settings?.owner_name} {settings?.professional_title}<br />{settings?.address} {settings?.cap} {settings?.comune} ({settings?.provincia})<br />{settings?.email} {settings?.phone}</div>
      </div>
      <div className="flex flex-wrap justify-between items-center gap-3 print-hidden">
      <Link to="/pratiche" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-800" data-testid="back-to-pratiche-link">
        <ArrowLeft className="h-4 w-4" /> Archivio pratiche
      </Link>
      <div className="flex flex-wrap gap-2">
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => window.open(`${window.location.origin}/portale/${practice.share_token}`, "_blank")}>
          <Link2 className="h-4 w-4 mr-2" />Apri link cliente
        </Button>
        <Button variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" />Stampa pratica</Button>
      </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
            {practice.protocol_code}
          </span>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATO_STYLES[practice.status] || ""}`} data-testid="pratica-status-badge">
            {statoLabel(practice.status)}
          </span>
          <span className="text-xs font-medium text-blue-800">{tipoLabel(practice.type, practiceTypes)}</span>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="pratica-title">
          {practice.title}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Cliente: <span className="font-medium text-slate-700">{practice.client?.nome || "—"}</span>
          {practice.comune ? ` · ${practice.comune}` : ""}
        </p>
        <div className="flex items-center gap-3 mt-4 max-w-md">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-800 rounded-full transition-all duration-500" style={{ width: `${practice.progress}%` }} />
          </div>
          <span className="font-mono text-xs font-semibold text-slate-600" data-testid="pratica-progress">{practice.progress}%</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="print-hidden flex flex-wrap h-auto gap-1 bg-white border border-slate-200 p-1.5 rounded-xl">
          <TabsTrigger value="dati" data-testid="tab-dati-generali">Dati Generali</TabsTrigger>
          <TabsTrigger value="foto">Foto</TabsTrigger>
          <TabsTrigger value="allegati" data-testid="tab-allegati-foto">Allegati</TabsTrigger>
          <TabsTrigger value="elaborati" data-testid="tab-elaborati">Elaborati</TabsTrigger>
          <TabsTrigger value="modulistica" data-testid="tab-modulistica">Modelli editabili</TabsTrigger>
          <TabsTrigger value="pdf" data-testid="tab-pdf">PDF</TabsTrigger>
          <TabsTrigger value="cliente" data-testid="tab-cliente">CLIENTE</TabsTrigger>
          <TabsTrigger value="scadenze" data-testid="tab-scadenze">Scadenze</TabsTrigger>
          <TabsTrigger value="note" data-testid="tab-note-interne">Note & Messaggi</TabsTrigger>
          <TabsTrigger value="link" data-testid="tab-link-cliente">Portale Cliente</TabsTrigger>
        </TabsList>

        <TabsContent value="dati"><DatiTab practice={practice} practiceTypes={practiceTypes} onSaved={loadAll} /></TabsContent>
        <TabsContent value="foto"><AllegatiTab mode="foto" practiceId={id} attachments={attachments} onChange={loadAll} /></TabsContent>
        <TabsContent value="allegati"><AllegatiTab mode="documento" practiceId={id} attachments={attachments} onChange={loadAll} /></TabsContent>
        <TabsContent value="elaborati"><ElaboratiTab practiceId={id} attachments={attachments} onChange={loadAll} /></TabsContent>
        <TabsContent value="modulistica"><PracticeFormsTab practiceId={id} attachments={attachments} onChange={loadAll} onPdfSaved={()=>setActiveTab("pdf")} /></TabsContent>
        <TabsContent value="pdf"><PdfFilesTab practiceId={id} attachments={attachments} onChange={loadAll} /></TabsContent>
        <TabsContent value="cliente"><AllegatiTab mode="cliente" practiceId={id} attachments={attachments} onChange={loadAll} /></TabsContent>
        <TabsContent value="scadenze"><ScadenzeTab practice={practice} onChange={loadAll} /></TabsContent>
        <TabsContent value="note"><NoteTab practiceId={id} comments={comments} onChange={loadAll} /></TabsContent>
        <TabsContent value="link"><LinkTab practice={practice} onChange={loadAll} /></TabsContent>
      </Tabs>
    </div>
  );
}

const ELABORATI_SECTIONS = [
  { value: "render", label: "Render", description: "Immagini, video e visualizzazioni del progetto" },
  { value: "dwg", label: "DWG", description: "Disegni tecnici e file CAD" },
  { value: "idee", label: "Idee", description: "Riferimenti, bozze e materiali di ispirazione" },
];

function PdfVisualPage({ page, fields, onChange }) {
  const canvasRef = useRef(null);
  const [annotations, setAnnotations] = useState([]);
  const viewport = page.getViewport({scale:1.35});
  useEffect(()=>{let cancelled=false;(async()=>{const view=page.getViewport({scale:1.35});const canvas=canvasRef.current;if(!canvas)return;canvas.width=view.width;canvas.height=view.height;canvas.style.width=`${view.width}px`;canvas.style.height=`${view.height}px`;await page.render({canvasContext:canvas.getContext("2d"),viewport:view}).promise;const items=(await page.getAnnotations()).filter(annotation=>annotation.subtype==="Widget"&&annotation.fieldName);if(!cancelled)setAnnotations(items);})();return()=>{cancelled=true};},[page]);
  return <div className="relative mx-auto bg-white shadow" style={{width:viewport.width,height:viewport.height}}><canvas ref={canvasRef} className="absolute inset-0"/>{annotations.map((annotation,index)=>{const field=fields.find(value=>value.name===annotation.fieldName);if(!field)return null;const rect=viewport.convertToViewportRectangle(annotation.rect);const left=Math.min(rect[0],rect[2]),top=Math.min(rect[1],rect[3]),width=Math.abs(rect[2]-rect[0]),height=Math.abs(rect[3]-rect[1]);const style={position:"absolute",left,top,width,height};if(field.type==="checkbox")return <input key={`${field.name}-${index}`} type="checkbox" checked={!!field.value} onChange={event=>onChange(field.name,event.target.checked)} style={style} className="accent-blue-800"/>;if(field.options.length)return <select key={`${field.name}-${index}`} value={field.value} onChange={event=>onChange(field.name,event.target.value)} style={style} className="border border-blue-400 bg-white/95 text-[10px] px-1"><option value=""></option>{field.options.map(option=><option key={option}>{option}</option>)}</select>;return <input key={`${field.name}-${index}`} value={field.value||""} onChange={event=>onChange(field.name,event.target.value)} style={style} className="border border-blue-400 bg-white/90 px-1 text-[11px] focus:ring-1 focus:ring-blue-700 outline-none"/>;})}</div>;
}

function PdfFormEditor({ item, practiceId, onClose, onSaved }) {
  const [pdfDoc, setPdfDoc] = useState(null),[pages,setPages]=useState([]),[fields,setFields]=useState([]),[loading,setLoading]=useState(true),[saving,setSaving]=useState(false);
  useEffect(()=>{let active=true;(async()=>{try{const response=await fetch(`${API_URL}/attachments/${item.id}/file`,{credentials:"include"});if(!response.ok)throw new Error();const bytes=await response.arrayBuffer();const doc=await PDFDocument.load(bytes.slice(0));const pdf=await pdfjsLib.getDocument({data:bytes.slice(0)}).promise;const loadedPages=[];for(let number=1;number<=pdf.numPages;number++)loadedPages.push(await pdf.getPage(number));const values=doc.getForm().getFields().map(field=>{let type="unsupported",value="",options=[];try{if(field instanceof PDFTextField){type="text";value=field.getText()||"";}else if(field instanceof PDFCheckBox){type="checkbox";value=field.isChecked();}else if(field instanceof PDFDropdown||field instanceof PDFOptionList){type="select";value=field.getSelected()?.[0]||"";options=field.getOptions();}else if(field instanceof PDFRadioGroup){type="radio";value=field.getSelected()||"";options=field.getOptions();}}catch{}return{name:field.getName(),type,value,options};}).filter(field=>field.type!=="unsupported");if(active){setPdfDoc(doc);setPages(loadedPages);setFields(values);}}catch{toast.error("Impossibile leggere questo PDF");onClose();}finally{if(active)setLoading(false);}})();return()=>{active=false};},[item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const update=(name,value)=>setFields(current=>current.map(field=>field.name===name?{...field,value}:field));
  const save=async()=>{setSaving(true);try{const form=pdfDoc.getForm();fields.forEach(({name,type,value})=>{const field=form.getField(name);if(type==="text")field.setText(value||"");else if(type==="checkbox"){if(value)field.check();else field.uncheck();}else if(type==="select"||type==="radio")field.select(value);});const editableBytes=await pdfDoc.save();const printedBlob=await createPrintedPdfBlob(editableBytes);const data=new FormData();data.append("title",`${item.title||item.filename} - compilato`);data.append("category","pdf");data.append("file",new File([printedBlob],item.filename||"modulo-compilato.pdf",{type:"application/pdf"}));await API.post(`/practices/${practiceId}/attachments`,data);toast.success("PDF definitivo salvato nella scheda PDF");await onSaved();onClose();}catch(error){toast.error(formatApiError(error,"Non è stato possibile salvare il PDF"));}finally{setSaving(false);}};
  return <Dialog open onOpenChange={open=>!open&&onClose()}><DialogContent className="bg-slate-200 w-[96vw] max-w-[1100px] h-[94vh] flex flex-col p-0 overflow-hidden"><div className="bg-white p-4 sm:px-6 border-b"><DialogHeader><DialogTitle>Compila il modulo originale</DialogTitle></DialogHeader><p className="text-sm text-slate-500 mt-1">Scrivi direttamente nei campi del PDF e salva nella pratica.</p></div>{loading?<div className="flex-1 flex items-center justify-center text-slate-500">Caricamento del modulo…</div>:fields.length===0?<div className="m-6 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">Questo PDF non contiene campi compilabili.</div>:<div className="flex-1 overflow-auto p-3 sm:p-6"><div className="space-y-5 min-w-max">{pages.map((page,index)=><PdfVisualPage key={index} page={page} fields={fields} onChange={update}/>)}</div></div>}<div className="bg-white border-t p-3 sm:px-6 flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Annulla</Button><Button type="button" disabled={saving||loading||!fields.length} onClick={save} className="bg-blue-800">{saving?"Salvataggio…":"Salva PDF nella pratica"}</Button></div></DialogContent></Dialog>;
}

function WordDocumentEditor({ item, practiceId, onClose, onSaved }) {
  const editorRef=useRef(null),[loading,setLoading]=useState(true),[saving,setSaving]=useState(false);
  useEffect(()=>{let active=true;(async()=>{try{const response=await fetch(`${API_URL}/attachments/${item.id}/file`,{credentials:"include"});if(!response.ok)throw new Error();const bytes=await response.arrayBuffer();if(editorRef.current){editorRef.current.innerHTML="";await renderDocx(bytes,editorRef.current,null,{className:"docx",inWrapper:true,ignoreWidth:false,ignoreHeight:false,breakPages:true,useBase64URL:true});editorRef.current.querySelectorAll("section.docx").forEach(page=>page.setAttribute("contenteditable","true"));}}catch{toast.error("Impossibile aprire questo documento Word");onClose();}finally{if(active)setLoading(false);}})();return()=>{active=false};},[item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save=async()=>{const pages=[...editorRef.current.querySelectorAll("section.docx")];if(!pages.length){toast.error("Documento Word non disponibile");return;}setSaving(true);try{let output=null;for(let index=0;index<pages.length;index++){const canvas=await html2canvas(pages[index],{scale:1.5,useCORS:true,backgroundColor:"#ffffff"});const orientation=canvas.width>canvas.height?"landscape":"portrait";if(!output)output=new jsPDF({orientation,unit:"pt",format:"a4"});else output.addPage("a4",orientation);const pageWidth=output.internal.pageSize.getWidth(),pageHeight=output.internal.pageSize.getHeight();const ratio=Math.min(pageWidth/canvas.width,pageHeight/canvas.height);const width=canvas.width*ratio,height=canvas.height*ratio;output.addImage(canvas.toDataURL("image/jpeg",0.95),"JPEG",(pageWidth-width)/2,0,width,height,undefined,"FAST");}const blob=output.output("blob");const baseName=(item.filename||"modello.docx").replace(/\.docx?$/i,"");const data=new FormData();data.append("title",`${item.title||baseName} - compilato`);data.append("category","pdf");data.append("file",new File([blob],`${baseName}-compilato.pdf`,{type:"application/pdf"}));await API.post(`/practices/${practiceId}/attachments`,data);toast.success("Documento Word salvato come PDF definitivo");await onSaved();onClose();}catch(error){toast.error(formatApiError(error,"Non è stato possibile creare il PDF"));}finally{setSaving(false);}};
  return <Dialog open onOpenChange={open=>!open&&onClose()}><DialogContent className="bg-slate-200 w-[96vw] max-w-[1200px] h-[94vh] flex flex-col p-0 overflow-hidden"><div className="bg-white p-4 sm:px-6 border-b"><DialogHeader><DialogTitle>Compila il modello Word originale</DialogTitle></DialogHeader><p className="text-sm text-slate-500 mt-1">Tocca il testo del documento per modificarlo. Il risultato sarà salvato come PDF definitivo.</p></div><div className="flex-1 overflow-auto p-3 sm:p-6">{loading&&<div className="py-16 text-center text-slate-500">Caricamento del documento Word…</div>}<div ref={editorRef} className="word-editor mx-auto [&_.docx-wrapper]:bg-transparent [&_section.docx]:shadow-lg [&_section.docx]:outline-none [&_section.docx:focus]:ring-2 [&_section.docx:focus]:ring-blue-500"/></div><div className="bg-white border-t p-3 sm:px-6 flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Annulla</Button><Button type="button" disabled={saving||loading} onClick={save} className="bg-blue-800">{saving?"Creazione PDF…":"Salva come PDF nella pratica"}</Button></div></DialogContent></Dialog>;
}

function PracticeFormsTab({ practiceId, attachments, onChange, onPdfSaved }) {
  const [editing, setEditing] = useState(null);
  const [uploadingCompleted,setUploadingCompleted]=useState(false);
  const forms = attachments.filter(item=>item.category==="modulistica");
  const completedPdfs = attachments.filter(item=>item.category==="pdf");
  const baseName=(name="")=>name.toLowerCase().replace(/\.(pdf|docx?|odt)$/i,"").replace(/\s*-\s*compilato$/i,"").trim();
  const isCompleted=(item)=>completedPdfs.some(pdf=>baseName(pdf.filename)===baseName(item.filename));
  const remove = async (id) => { if(!window.confirm("Eliminare questo modulo dalla pratica?"))return;try{await API.delete(`/attachments/${id}`);toast.success("Modulo eliminato dalla pratica");onChange();}catch(error){toast.error(formatApiError(error));} };
  const replace = async (event) => { event.preventDefault();if(uploadingCompleted)return;if(!editing.file){toast.error("Seleziona il PDF compilato da salvare");return;}setUploadingCompleted(true);try{const data=new FormData();data.append("title",editing.title||editing.filename);data.append("file",editing.file);const existing=completedPdfs.find(pdf=>baseName(pdf.filename)===baseName(editing.file.name));if(existing)await API.put(`/attachments/${existing.id}`,data);else{data.append("category","pdf");await API.post(`/practices/${practiceId}/attachments`,data);}toast.success(existing?"PDF compilato aggiornato":"File compilato salvato nella scheda PDF");setEditing(null);await onChange();onPdfSaved();}catch(error){toast.error(formatApiError(error));}finally{setUploadingCompleted(false);} };
  if(!forms.length)return <div className="bg-white rounded-xl border p-10 text-center"><FileText className="h-9 w-9 text-slate-300 mx-auto mb-3"/><p className="text-sm text-slate-500">Nessun modulo inviato a questa pratica.</p><Link to="/modulistica" className="inline-flex mt-3 text-sm font-semibold text-blue-800 hover:underline">Apri l’archivio Modulistica</Link></div>;
  return <div className="space-y-3"><div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-slate-700"><strong className="text-slate-900">Modelli originali:</strong> apri o scarica il documento, compilalo esternamente e carica il PDF compilato. Il modello originale resta invariato.</div><div className="bg-white rounded-xl border shadow-sm divide-y divide-slate-100 overflow-hidden">{forms.map(item=><div key={item.id} className="p-4 flex flex-col xl:flex-row xl:items-center gap-4"><div className="flex gap-3 min-w-0 flex-1"><div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center shrink-0"><FileText className="h-5 w-5"/></div><div className="min-w-0"><div className="flex flex-wrap gap-2 mb-1"><span className="text-[10px] font-semibold px-2 py-1 rounded bg-blue-100 text-blue-900">{item.procedure_type}</span><span className="text-[10px] font-semibold px-2 py-1 rounded bg-amber-100 text-amber-900">{item.office}</span>{item.municipality&&<span className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-900">{item.municipality}</span>}{isCompleted(item)&&<span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded bg-emerald-600 text-white"><span className="h-2 w-2 rounded-full bg-white"/>Compilato</span>}</div><p className="font-semibold text-slate-900 truncate">{item.title||item.filename}</p><p className="text-xs text-slate-400 truncate">{item.filename} · {(item.size/1024).toFixed(0)} KB</p></div></div><div className="flex flex-wrap items-center gap-2 xl:shrink-0"><button type="button" onClick={()=>window.open(`${API_URL}/attachments/${item.id}/open`,"_blank")} className="h-10 px-4 rounded-md border text-sm font-medium text-blue-800 hover:bg-blue-50">Apri</button><button type="button" onClick={()=>setEditing({...item,file:null})} className="h-10 px-4 rounded-md bg-blue-800 text-white text-sm font-medium hover:bg-blue-900"><Upload className="h-4 w-4 inline mr-2"/>Carica compilato</button><span className="inline-flex gap-3 px-2"><FileTransferActions shareEnabled={false} url={`${API_URL}/attachments/${item.id}/file`} filename={item.filename} title={item.title}/></span><button type="button" className="h-10 w-10 inline-flex items-center justify-center rounded-md border hover:bg-red-50" title="Elimina" onClick={()=>remove(item.id)}><Trash2 className="h-4 w-4 text-red-600"/></button></div></div>)}{editing&&<Dialog open onOpenChange={open=>!open&&setEditing(null)}><DialogContent className="bg-white"><DialogHeader><DialogTitle>Carica modello compilato</DialogTitle></DialogHeader><form onSubmit={replace} className="space-y-4"><p className="text-sm text-slate-500">Carica il modello compilato in formato PDF. Verrà aggiunto nella scheda PDF; se mantiene lo stesso nome del modello apparirà l’indicatore verde “Compilato”.</p><div><Label>Nome</Label><Input value={editing.title||""} onChange={e=>setEditing({...editing,title:e.target.value})}/></div><div><Label>PDF compilato *</Label><Input required accept="application/pdf,.pdf" className="mt-2" type="file" onChange={e=>setEditing({...editing,file:e.target.files?.[0]||null})}/></div><Button disabled={uploadingCompleted} className="w-full bg-blue-800">{uploadingCompleted?"Caricamento…":"Carica nella scheda PDF"}</Button></form></DialogContent></Dialog>}</div></div>;
}

function PdfFilesTab({ practiceId, attachments, onChange }) {
  const [title,setTitle]=useState(""),[uploading,setUploading]=useState(false),[editing,setEditing]=useState(null);
  const files=attachments.filter(item=>item.category==="pdf");
  const upload=async(event)=>{const file=event.target.files?.[0];event.target.value="";if(!file)return;if(file.type!=="application/pdf"&&!file.name.toLowerCase().endsWith(".pdf")){toast.error("Seleziona un file PDF");return;}setUploading(true);try{const data=new FormData();data.append("file",file);data.append("category","pdf");data.append("title",title.trim()||file.name);await API.post(`/practices/${practiceId}/attachments`,data);toast.success("PDF caricato");setTitle("");onChange();}catch(error){toast.error(formatApiError(error));}finally{setUploading(false);}};
  const remove=async(id)=>{if(!window.confirm("Eliminare questo PDF?"))return;try{await API.delete(`/attachments/${id}`);await onChange();toast.success("PDF eliminato");}catch(error){toast.error(formatApiError(error));}};
  const update=async(event)=>{event.preventDefault();const data=new FormData();data.append("title",editing.title||editing.filename);if(editing.file)data.append("file",editing.file);try{await API.put(`/attachments/${editing.id}`,data);toast.success("PDF aggiornato");setEditing(null);onChange();}catch(error){toast.error(formatApiError(error));}};
  return <div className="space-y-5"><div className="bg-white rounded-xl border p-5"><h3 className="font-heading font-semibold mb-4">Carica PDF</h3><div className="grid sm:grid-cols-[1fr_auto] gap-3"><Input value={title} onChange={event=>setTitle(event.target.value)} placeholder="Nome del documento (facoltativo)"/><label className="h-10 px-4 inline-flex items-center justify-center rounded-md bg-blue-800 text-white text-sm font-medium cursor-pointer hover:bg-blue-900"><Upload className="h-4 w-4 mr-2"/>{uploading?"Caricamento…":"Seleziona PDF"}<input hidden disabled={uploading} type="file" accept="application/pdf,.pdf" onChange={upload}/></label></div></div><div className="bg-white rounded-xl border shadow-sm divide-y divide-slate-100 overflow-hidden">{files.length===0?<p className="p-10 text-center text-sm text-slate-500">Nessun PDF caricato.</p>:files.map(item=><div key={item.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-4"><div className="flex items-center gap-3 min-w-0 flex-1"><div className="h-10 w-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center shrink-0"><FileText className="h-5 w-5"/></div><div className="min-w-0"><p className="font-semibold text-slate-900 truncate">{item.title||item.filename}</p><p className="text-xs text-slate-400 truncate">{item.filename} · {(item.size/1024).toFixed(0)} KB</p></div></div><div className="flex items-center gap-3"><button type="button" onClick={()=>window.open(`${API_URL}/attachments/${item.id}/open`,"_blank")} className="h-10 px-4 rounded-md border text-sm font-medium text-blue-800 hover:bg-blue-50">Apri</button><FileTransferActions url={`${API_URL}/attachments/${item.id}/file`} filename={item.filename} title={item.title}/><button type="button" title="Modifica o sostituisci" onClick={()=>setEditing({...item,file:null})}><Pencil className="h-4 w-4 text-blue-800"/></button><button type="button" title="Elimina" onClick={()=>remove(item.id)}><Trash2 className="h-4 w-4 text-red-600"/></button></div></div>)}</div>{editing&&<Dialog open onOpenChange={open=>!open&&setEditing(null)}><DialogContent className="bg-white"><DialogHeader><DialogTitle>Modifica PDF</DialogTitle></DialogHeader><form onSubmit={update} className="space-y-4"><div><Label>Nome</Label><Input className="mt-2" value={editing.title||""} onChange={event=>setEditing({...editing,title:event.target.value})}/></div><div><Label>Sostituisci PDF (facoltativo)</Label><Input className="mt-2" type="file" accept="application/pdf,.pdf" onChange={event=>setEditing({...editing,file:event.target.files?.[0]||null})}/></div><Button className="w-full bg-blue-800">Salva modifiche</Button></form></DialogContent></Dialog>}</div>;
}

function ElaboratiTab({ practiceId, attachments, onChange }) {
  return (
    <Tabs defaultValue="render" className="space-y-4" data-testid="elaborati-tab">
      <TabsList className="flex flex-wrap h-auto gap-1 bg-white border border-slate-200 p-1.5 rounded-xl">
        {ELABORATI_SECTIONS.map((section) => (
          <TabsTrigger key={section.value} value={section.value} data-testid={`elaborati-${section.value}-tab`}>
            {section.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {ELABORATI_SECTIONS.map((section) => (
        <TabsContent key={section.value} value={section.value}>
          <ElaboratiFiles
            section={section}
            practiceId={practiceId}
            attachments={attachments}
            onChange={onChange}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}

function ElaboratiFiles({ section, practiceId, attachments, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const files = attachments.filter((attachment) => attachment.category === section.value);

  const upload = async (selectedFiles) => {
    if (!selectedFiles?.length) return;
    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", section.value);
        formData.append("title", file.name);
        await API.post(`/practices/${practiceId}/attachments`, formData);
      }
      toast.success(selectedFiles.length > 1 ? "File caricati" : "File caricato");
      onChange();
    } catch (error) {
      toast.error(formatApiError(error, "Errore durante il caricamento"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (attachmentId) => {
    if (!window.confirm("Eliminare questo file?")) return;
    try {
      await API.delete(`/attachments/${attachmentId}`);
      toast.success("File eliminato");
      onChange();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const update = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append("title", editingFile.title || editingFile.filename);
    if (editingFile.file) formData.append("file", editingFile.file);
    try {
      await API.put(`/attachments/${editingFile.id}`, formData);
      toast.success("File aggiornato");
      setEditingFile(null);
      onChange();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <div className="space-y-6" data-testid={`elaborati-${section.value}-content`}>
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6">
        <div className="mb-4">
          <h3 className="font-heading text-base font-semibold text-slate-900">{section.label}</h3>
          <p className="text-sm text-slate-500 mt-1">{section.description}. Puoi caricare qualsiasi tipo di file.</p>
        </div>
        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl px-6 py-5 cursor-pointer hover:border-blue-800 hover:bg-blue-50/50 transition-colors">
          <Upload className="h-5 w-5 text-slate-400" />
          <span className="text-sm text-slate-600">{uploading ? "Caricamento in corso..." : "Seleziona uno o più file"}</span>
          <input type="file" multiple className="hidden" disabled={uploading} onChange={(event) => { upload([...event.target.files]); event.target.value = ""; }} />
        </label>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-800" /> File {section.label} ({files.length})
        </h3>
        {files.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun file caricato.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {files.map((attachment) => (
              <li key={attachment.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{attachment.title || attachment.filename}</p>
                  <p className="text-xs text-slate-500 truncate">File: {attachment.filename}</p>
                  <p className="text-xs text-slate-400">{formatDate(attachment.created_at)} · {attachment.uploaded_by} · {(attachment.size / 1024).toFixed(0)} KB</p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" title="Modifica" onClick={() => setEditingFile({ ...attachment, file: null })} className="text-slate-400 hover:text-blue-800"><Pencil className="h-4 w-4" /></button>
                  <FileTransferActions url={`${API_URL}/attachments/${attachment.id}/file`} filename={attachment.filename} title={attachment.title} />
                  <button type="button" title="Elimina" onClick={() => remove(attachment.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editingFile && (
        <Dialog open onOpenChange={(open) => !open && setEditingFile(null)}>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>Modifica o sostituisci file</DialogTitle></DialogHeader>
            <form onSubmit={update} className="space-y-4">
              <div><Label>Nome file</Label><Input required value={editingFile.title || ""} onChange={(event) => setEditingFile({ ...editingFile, title: event.target.value })} /></div>
              <div><Label>Sostituisci file (facoltativo)</Label><Input type="file" onChange={(event) => setEditingFile({ ...editingFile, file: event.target.files?.[0] })} /></div>
              <Button className="bg-blue-800">Salva modifiche</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function DatiTab({ practice, practiceTypes, onSaved }) {
  const [form, setForm] = useState({ ...practice });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put(`/practices/${practice.id}`, {
        title: form.title, type: form.type, provincia: form.provincia, comune: form.comune, address: form.address,
        description: form.description, status: form.status, progress: Number(form.progress),
      });
      toast.success("Pratica aggiornata");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6 space-y-4" data-testid="dati-generali-form">
      <div className="space-y-2">
        <Label>Titolo pratica</Label>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-pratica-title-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tipologia</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="edit-pratica-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {!practiceTypes.some((t) => t.value === form.type) && <SelectItem value={form.type}>{tipoLabel(form.type)}</SelectItem>}
              {practiceTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Stato</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="edit-pratica-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATI_PRATICA.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <LocationSelect provincia={form.provincia || ""} comune={form.comune || ""} onChange={(v) => setForm({ ...form, ...v })} />
      <div className="space-y-2"><Label>Indirizzo / Ubicazione</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-pratica-address-input" /></div>
      <div className="space-y-2">
        <Label>Avanzamento lavori (%)</Label>
        <Input type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} data-testid="edit-pratica-progress-input" />
      </div>
      <div className="space-y-2">
        <Label>Descrizione lavori</Label>
        <Textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-pratica-description-input" />
      </div>
      {practice.client && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Cliente</p>
          <p className="text-sm font-semibold text-slate-800">{practice.client.nome}</p>
          <p className="text-xs text-slate-500">{practice.client.email} {practice.client.telefono ? `· ${practice.client.telefono}` : ""}</p>
        </div>
      )}
      <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="save-pratica-button">
        {saving ? "Salvataggio..." : "Salva modifiche"}
      </Button>
    </form>
  );
}

function AllegatiTab({ mode, practiceId, attachments, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const [docScope, setDocScope] = useState("pratica");
  const [editingDoc, setEditingDoc] = useState(null);
  const isDocumentMode = mode === "documento" || mode === "cliente";

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", mode);
        if (isDocumentMode) fd.append("title", docTitle.trim() || f.name);
        if (mode === "documento") fd.append("document_scope", docScope);
        if (mode === "cliente") fd.append("document_scope", "cliente");
        await API.post(`/practices/${practiceId}/attachments`, fd);
      }
      toast.success(files.length > 1 ? "File caricati" : "Documento caricato");
      onChange();
      setDocTitle("");
    } catch (e) {
      toast.error(formatApiError(e, "Errore durante il caricamento"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (attId) => {
    if (!window.confirm("Eliminare questo file?")) return;
    try {
      await API.delete(`/attachments/${attId}`);
      toast.success("File eliminato");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fotos = attachments.filter((a) => a.category === "foto");
  const docs = attachments.filter((a) => a.category === mode);
  const replaceDoc = async (e) => { e.preventDefault(); const fd = new FormData(); fd.append("title", editingDoc.title || ""); if (editingDoc.file) fd.append("file", editingDoc.file); try { await API.put(`/attachments/${editingDoc.id}`, fd); toast.success("Documento aggiornato"); setEditingDoc(null); onChange(); } catch(err) { toast.error(formatApiError(err)); } };

  return (
    <div className="space-y-6" data-testid="allegati-tab">
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          {isDocumentMode && <><div className="space-y-2 flex-1"><Label>Nome del documento (facoltativo)</Label><Input value={docTitle} onChange={e=>setDocTitle(e.target.value)} placeholder="Se vuoto usa il nome del file" /></div>{mode === "documento" && <div className="space-y-2"><Label>Archivio</Label><select className="h-10 border rounded-md px-3" value={docScope} onChange={e=>setDocScope(e.target.value)}><option value="pratica">Documenti della pratica</option><option value="cliente">Documenti del cliente</option></select></div>}</>}
          <label
            className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl px-6 py-5 cursor-pointer hover:border-blue-800 hover:bg-blue-50/50 transition-colors"
            data-testid="upload-file-dropzone"
          >
            <Upload className="h-5 w-5 text-slate-400" />
            <span className="text-sm text-slate-600">
              {uploading ? "Caricamento in corso..." : mode === "foto" ? "Carica una o più foto (ottimizzazione automatica)" : "Seleziona uno o più file di qualsiasi formato"}
            </span>
            <input type="file" multiple accept={mode === "foto" ? "image/*" : "*/*"} className="hidden" disabled={uploading} onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="upload-file-input" />
          </label>
        </div>
      </div>

      {mode === "foto" && <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-blue-800" /> Galleria foto ({fotos.length})
        </h3>
        {fotos.length === 0 ? (
          <p className="text-sm text-slate-500">Nessuna foto caricata.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {fotos.map((a) => (
              <div key={a.id} className="group relative rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100" data-testid="foto-item">
                <img
                  src={`${API_URL}/attachments/${a.id}/file`}
                  alt={a.filename}
                  className="h-full w-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300"
                  onClick={() => setLightbox(a)}
                />
                <button
                  onClick={() => remove(a.id)}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  data-testid="foto-delete-button"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>}

      {isDocumentMode && <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-800" /> {mode === "cliente" ? "Documenti visibili al cliente" : "Documenti cliente e pratica"} ({docs.length})
        </h3>
        {docs.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun documento caricato.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {docs.map((a) => (
              <li key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid="documento-item">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><p className="text-sm font-medium text-slate-800 truncate">{a.title || a.filename}</p>{mode === "documento" && <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.document_scope==='cliente'?'bg-amber-100 text-amber-900':'bg-blue-100 text-blue-900'}`}>{a.document_scope==='cliente'?'Cliente':'Pratica'}</span>}</div><p className="text-xs text-slate-500 truncate">File: {a.filename}</p>
                  <p className="text-xs text-slate-400">{formatDate(a.created_at)} · {a.uploaded_by} · {(a.size / 1024).toFixed(0)} KB</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditingDoc({ ...a, file:null })} className="text-slate-400 hover:text-blue-800"><Pencil className="h-4 w-4" /></button>
                  <FileTransferActions url={`${API_URL}/attachments/${a.id}/file`} filename={a.filename} title={a.title} />
                  <button onClick={() => remove(a.id)} className="text-slate-400 hover:text-red-600" data-testid="documento-delete-button">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>}
      {editingDoc && <Dialog open onOpenChange={o=>!o&&setEditingDoc(null)}><DialogContent className="bg-white"><DialogHeader><DialogTitle>Modifica o sostituisci documento</DialogTitle></DialogHeader><form onSubmit={replaceDoc} className="space-y-4"><div><Label>Nome documento</Label><Input required value={editingDoc.title || ""} onChange={e=>setEditingDoc({...editingDoc,title:e.target.value})}/></div><div><Label>Sostituisci file (facoltativo)</Label><Input type="file" onChange={e=>setEditingDoc({...editingDoc,file:e.target.files?.[0]})}/></div><Button className="bg-blue-800">Salva modifiche</Button></form></DialogContent></Dialog>}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="foto-lightbox">
          <button className="absolute top-4 right-4 text-white"><X className="h-7 w-7" /></button>
          <img src={`${API_URL}/attachments/${lightbox.id}/file`} alt={lightbox.filename} className="max-h-full max-w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}

function ScadenzeTab({ practice, onChange }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");

  const save = async (deadlines) => {
    try {
      await API.put(`/practices/${practice.id}`, { deadlines });
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim() || !date) return;
    await save([...(practice.deadlines || []), { title: title.trim(), date }]);
    setTitle("");
    setDate("");
    toast.success("Scadenza aggiunta");
  };

  const deadlines = [...(practice.deadlines || [])].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6 space-y-5" data-testid="scadenze-tab">
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="space-y-2 flex-1">
          <Label>Incombenza</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Integrazione documentale SUE" data-testid="new-scadenza-title-input" />
        </div>
        <div className="space-y-2">
          <Label>Data scadenza</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="new-scadenza-date-input" />
        </div>
        <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="add-scadenza-button">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      </form>
      {deadlines.length === 0 ? (
        <p className="text-sm text-slate-500">Nessuna scadenza registrata.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {deadlines.map((d, i) => (
            <li key={i} className="py-3 flex items-center justify-between gap-3" data-testid="scadenza-item">
              <p className="text-sm font-medium text-slate-800">{d.title}</p>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-semibold px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200">
                  {formatDate(d.date)}
                </span>
                <button
                  onClick={() => save(practice.deadlines.filter((_, j) => practice.deadlines.indexOf(d) !== j))}
                  className="text-slate-400 hover:text-red-600"
                  data-testid="scadenza-delete-button"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NoteTab({ practiceId, comments, onChange }) {
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("pubblico");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await API.post(`/practices/${practiceId}/comments`, { text: text.trim(), visibility });
      setText("");
      toast.success(visibility === "interna" ? "Nota interna salvata" : "Messaggio pubblicato");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const remove = async (cid) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    await API.delete(`/comments/${cid}`).catch(() => {});
    onChange();
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6 space-y-5" data-testid="note-tab">
      <form onSubmit={send} className="space-y-3">
        <Textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Scrivi una nota o un messaggio per il cliente..."
          data-testid="new-comment-input"
        />
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <Select value={visibility} onValueChange={setVisibility}>
            <SelectTrigger className="w-full sm:w-64" data-testid="comment-visibility-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pubblico">Visibile al cliente</SelectItem>
              <SelectItem value="interna">Nota interna (solo studio)</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={sending} className="bg-blue-800 hover:bg-blue-900" data-testid="send-comment-button">
            {sending ? "Invio..." : "Pubblica"}
          </Button>
        </div>
      </form>
      {comments.length === 0 ? (
        <p className="text-sm text-slate-500">Nessun messaggio.</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li key={c.id} className={`rounded-lg border p-4 ${c.source === "cliente" ? "bg-amber-50/60 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid="comment-item">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">{c.author}</span>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${c.source === "cliente" ? "bg-amber-200 text-amber-900" : "bg-blue-100 text-blue-800"}`}>
                    {c.source === "cliente" ? "Cliente" : "Studio"}
                  </span>
                  {c.visibility === "interna" && (
                    <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                      <Lock className="h-2.5 w-2.5" /> Interna
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400">{formatDate(c.created_at)}</span>
                  <button onClick={() => remove(c.id)} className="text-slate-300 hover:text-red-600" data-testid="comment-delete-button">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LinkTab({ practice, onChange }) {
  const shareUrl = `${window.location.origin}/portale/${practice.share_token}`;
  const defaults = {portal_enabled:true,show_details:true,show_progress:true,show_deadlines:true,show_photos:true,show_documents:true,show_render:false,show_dwg:false,show_idee:false,allow_messages:true,allow_document_upload:false};
  const [portalSettings, setPortalSettings] = useState({...defaults,...(practice.portal_settings||{})});

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link cliente copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const shareWithInstructions = async () => {
    const instructions = `Apri il portale Arch. CLG:\n${shareUrl}\n\nPer aggiungerlo alla Home:\n• iPhone/iPad con Safari: tocca Condividi e poi “Aggiungi alla schermata Home”.\n• iPhone/iPad con Chrome: tocca Condividi, scorri le azioni e scegli “Aggiungi alla schermata Home”.\n• Android con Chrome: tocca il menu ⋮ e poi “Installa app” o “Aggiungi a schermata Home”.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Arch. CLG · Portale Cliente", text: instructions, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(instructions);
        toast.success("Messaggio e istruzioni copiati negli appunti");
      }
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Impossibile condividere il link");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Rigenerare il link? Il link precedente smetterà di funzionare.")) return;
    try {
      await API.post(`/practices/${practice.id}/share/regenerate`);
      toast.success("Nuovo link generato");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const savePortalSettings = async () => { try { await API.put(`/practices/${practice.id}/portal-settings`, portalSettings); toast.success("Permessi del portale salvati"); onChange(); } catch(e) { toast.error(formatApiError(e)); } };
  const togglePortalAccess = async (enabled) => {
    const updated = { ...portalSettings, portal_enabled: enabled };
    setPortalSettings(updated);
    try {
      await API.put(`/practices/${practice.id}/portal-settings`, updated);
      toast.success(enabled ? "Accesso cliente riattivato" : "Accesso cliente sospeso");
      onChange();
    } catch (error) {
      setPortalSettings(portalSettings);
      toast.error(formatApiError(error, "Impossibile aggiornare l’accesso al portale"));
    }
  };
  const options = [["show_details","Dati e stato della pratica"],["show_progress","Percentuale di avanzamento"],["show_deadlines","Scadenze"],["allow_messages","Leggere e inviare messaggi"],["allow_document_upload","Caricare documenti per lo studio"]];

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6 space-y-5" data-testid="link-tab">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
          <Link2 className="h-5 w-5 text-blue-800" />
        </div>
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">Link di condivisione cliente</h3>
          <p className="text-sm text-slate-500 mt-1">
            Il cliente può aprire questo link senza registrarsi: vedrà stato della pratica, foto, documenti e potrà lasciare messaggi.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input readOnly value={shareUrl} className="font-mono text-xs bg-slate-50" data-testid="share-link-input" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={copy} className="bg-blue-800 hover:bg-blue-900" data-testid="copy-share-link-button">
            <Copy className="h-4 w-4 mr-1.5" /> Copia
          </Button>
          <Button variant="outline" onClick={shareWithInstructions} data-testid="share-client-link-button">
            <Share2 className="h-4 w-4 mr-1.5" /> Condividi
          </Button>
          <Button variant="outline" onClick={() => window.open(shareUrl, "_blank")} data-testid="preview-portal-button">
            Anteprima
          </Button>
          <Button variant="outline" onClick={regenerate} data-testid="generate-client-link-button">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Rigenera
          </Button>
        </div>
      </div>
      <div className="border-t pt-5 space-y-4">
        <label className={`flex items-start gap-3 rounded-xl border p-4 ${portalSettings.portal_enabled ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
          <input type="checkbox" className="mt-1 h-4 w-4" checked={!!portalSettings.portal_enabled} onChange={e=>togglePortalAccess(e.target.checked)}/>
          <span><strong className="block text-sm">Accesso al portale cliente attivo</strong><span className="text-xs text-slate-600">Se disattivato, aprendo il link il cliente vedrà esclusivamente il logo dello studio.</span></span>
        </label>
        <div className={portalSettings.portal_enabled ? "" : "opacity-50 pointer-events-none"}><h3 className="font-heading font-semibold mb-3">Cosa può vedere e fare il cliente</h3><div className="grid sm:grid-cols-2 gap-2">{options.map(([key,label])=><label key={key} className="flex items-center gap-3 border rounded-lg p-3 text-sm"><input type="checkbox" checked={!!portalSettings[key]} onChange={e=>setPortalSettings({...portalSettings,[key]:e.target.checked})}/>{label}</label>)}</div></div>
        <Button type="button" onClick={savePortalSettings} className="bg-blue-800">Salva permessi portale</Button>
      </div>
    </div>
  );
}
